import { Controller } from "@hotwired/stimulus"
import { syncWhoLivesIntoCircle } from "who_lives_link"
import { savePlan, registerFlush, restoreFromIdbIfNeeded } from "plan_storage"
import { requestPersistentStorage } from "plan_backup"

// Plan-store controller — single source of truth for the in-browser plan
// object across every native-editor section. Lives on the <body> (via
// data-controller="plan-store") so every section's Stimulus controller
// can find it with stimulus's outlets mechanism or via direct lookup.
//
// Bindings on an input:
//   data-action="input->plan-store#update"
//   data-plan-field="client.firstName"
//
// On connect we hydrate inputs from the saved plan. On every change
// we save the entire plan back to localStorage under the SAME key the
// iframe editor uses, so /editor and /editor/native share the same
// plan data and stay in sync.
const STORAGE_KEY  = "pcp_plan_data"
const AUTOSAVE_MS  = 250

export default class extends Controller {
  // NOTE: the `{ type: Number, default: 250 }` static-values form requires
  // Stimulus 3.2+; we hardcode AUTOSAVE_MS instead so we work on whatever
  // Stimulus version Rails 8.1's importmap pin resolves to.

  connect() {
    this.plan = this._load()
    this._hydrate()
    // Re-hydrate when a different section becomes visible (other ports may
    // have just been swapped in / out of hidden).
    this.observer = new MutationObserver(() => this._hydrate())
    this.observer.observe(this.element, { attributes: true, attributeFilter: ["hidden"], subtree: true })
    // Same-tab sync — when the embedded wizard (or any other controller)
    // dispatches plan:change after writing to localStorage, reload our
    // in-memory plan and re-hydrate the editor's inputs so changes in
    // the wizard pane show up in the editor pane live (and the other
    // direction is already covered by the wizard's own plan:change
    // listener). Self-fired events are skipped so we don't loop.
    this._onPlanChange = (e) => {
      if (this._suppressNextChange) return
      if (e.detail?.source === "plan-store") return
      // Only rehydrate the editor inputs for changes this pane can't already
      // see: WIZARD-pane edits (the split-view mirror) and whole-plan swaps
      // (undo/redo, open .sdp, new/sample plan → path "*"). Every per-field
      // EDITOR edit already lives in the input that changed, so rehydrating
      // on those re-set the ENTIRE form on every keystroke — the main cause
      // of the typing lag (one edit → dozens of full-form rehydrates).
      if (e.detail?.source !== "wizard" && e.detail?.path !== "*") return
      this.plan = e.detail?.plan || this._load()
      this._hydrate()
    }
    document.addEventListener("plan:change", this._onPlanChange)
    // Cross-tab safety net — localStorage `storage` event fires only in
    // OTHER tabs/windows when our key changes, but covers the case
    // where the wizard is opened in a second tab and writes to the
    // same plan.
    this._onStorage = (e) => {
      if (e.key !== STORAGE_KEY || e.newValue == null) return
      try {
        this.plan = JSON.parse(e.newValue) || {}
        this._hydrate()
      } catch (_) {}
    }
    window.addEventListener("storage", this._onStorage)
    // Last-chance flush: run a pending scheduled save when the tab hides/closes.
    this._unregFlush = registerFlush(() => this._flushPending())
    // Ask for persistent storage (editor only — where the plan lives) so an
    // inactive plan resists ITP/eviction. Best-effort + guarded.
    requestPersistentStorage()
    // Durability: if localStorage was wiped (ITP eviction / cleared) but the
    // IndexedDB mirror still holds a backup, restore it and re-hydrate every
    // pane. Only fires when there's no live plan — never overwrites current work.
    restoreFromIdbIfNeeded().then(restored => {
      if (!restored) return
      this.plan = restored
      this._hydrate()
      this.element.dispatchEvent(new CustomEvent("plan:change", {
        bubbles: true, detail: { path: "*", plan: restored, source: "idb-restore" }
      }))
    }).catch(() => {})
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    document.removeEventListener("plan:change", this._onPlanChange)
    window.removeEventListener("storage", this._onStorage)
    this._unregFlush?.()
    this._flushPending()
  }

  // Run any pending debounced save NOW (was previously dropped on teardown).
  _flushPending() {
    if (this._saveTimer) { clearTimeout(this._saveTimer); this._saveTimer = null; this._save() }
  }

  // Called from every form input via data-action="input->plan-store#update"
  update(event) {
    const el = event.currentTarget
    const path = el.dataset.planField
    if (!path) return
    const value = (el.type === "checkbox") ? !!el.checked : el.value
    this._set(path, value)
    // Save synchronously so any listener that re-reads localStorage
    // sees fresh data on the very next tick. The 250ms debounce that
    // used to live here let the wizard pane pick up stale values when
    // it reloaded from localStorage after every plan:change.
    this._save()
    // Notify other controllers (including the embedded wizard) that
    // something changed. detail.source = "plan-store" so our own
    // plan:change listener skips events we fired ourselves.
    this._suppressNextChange = true
    this.element.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path, value, plan: this.plan, source: "plan-store" }
    }))
    this._suppressNextChange = false
  }

  // Public API for sibling controllers
  get(path)   { return this._get(path) }
  set(path, v){ this._set(path, v); this._scheduleSave() }
  data()      { return this.plan }

  _hydrate() {
    const active = document.activeElement
    this.element.querySelectorAll("[data-plan-field]").forEach(el => {
      // Skip the input the user is currently typing in — otherwise a
      // plan:change from the wizard pane could clobber their
      // in-progress keystrokes with a stale value from localStorage.
      if (el === active) return
      const path = el.dataset.planField
      const val = this._get(path)
      if (val === undefined || val === null) return
      if (el.type === "checkbox") {
        el.checked = !!val
      } else if (el.value !== String(val)) {
        el.value = val
      }
    })
  }

  _scheduleSave() {
    clearTimeout(this._saveTimer)
    this._saveTimer = setTimeout(() => this._save(), AUTOSAVE_MS)
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }

  _save() {
    syncWhoLivesIntoCircle(this.plan)
    // savePlan surfaces a visible warning on quota overflow instead of
    // swallowing it, so oversized plans never lose data silently.
    return savePlan(this.plan)
  }

  _get(path) {
    return path.split(".").reduce((o, k) => (o && o[k] !== undefined) ? o[k] : undefined, this.plan)
  }

  _set(path, value) {
    const parts = path.split(".")
    const last = parts.pop()
    let node = this.plan
    parts.forEach(k => {
      if (!node[k] || typeof node[k] !== "object") node[k] = {}
      node = node[k]
    })
    node[last] = value
  }
}
